import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import { EmploiDuTemps } from '@teetsh/app/src/types/edt';
import { addToEDTPast } from '@teetsh/app/src/queries/edt/historyAtoms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

export default function useDeleteEDTEvents(
  edtId: string,
  disableUndoRedo: boolean = false,
) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/emplois-du-temps`;
  const { t } = useTranslation(['emploiDuTemps']);
  const [, addToPast] = useAtom(addToEDTPast);
  const { mutateAsync } = useMutation({
    mutationFn: (eventsIds: string[]) =>
      request(`${requestURL}/${edtId}/seances`, {
        method: 'DELETE',
        body: { ids: eventsIds },
      }),
    onMutate: (eventsIds: string[]) => {
      if (!disableUndoRedo) {
        const oldEDT = queryClient.getQueryData<EmploiDuTemps>([
          schoolyear.id,
          school.id,
          EDT_KEY,
          edtId,
        ]);
        if (oldEDT) {
          addToPast({ edtId: oldEDT.id, seances: oldEDT.events });
        }
      }
      queryClient.setQueryData<EmploiDuTemps>(
        [schoolyear.id, school.id, EDT_KEY, edtId],
        (oldEDT: EmploiDuTemps) => {
          const oldEvents = oldEDT?.events || [];
          return {
            ...oldEDT,
            events: oldEvents.filter(evt => !eventsIds.includes(evt.id)),
          };
        },
      );
    },
    onError: (_, eventsIds) => {
      queryClient.invalidateQueries({
        queryKey: [schoolyear.id, school.id, EDT_KEY, edtId],
      });
      notifyError({
        title: t('emploiDuTemps:messages.seance.deleteError', {
          count: (eventsIds || []).length,
        }),
      });
    },
  });
  return mutateAsync;
}
